<script setup>
import { ref, computed } from "vue";
import { Inertia } from '@inertiajs/inertia';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import { Link, Head, usePage } from '@inertiajs/inertia-vue3';
import PremiumPageHeader from "@/Components/PremiumPageHeader.vue";
import Buttons from "@/Components/Buttons.vue";
import TableFilter from "@/Components/TableFilter.vue";
import CardBoxModal from "@/Components/CardBoxModal.vue";
import { Icon } from '@iconify/vue';
import axios from 'axios';

const props = defineProps({
    comunicados: [Array, Object],
    targetTypeOptions: Array,
    canWrite: Boolean,
    canDelete: Boolean,
    userParam: [Array, Object],
    useFilterAnio: Number,
});

const rutaPrefijo = ref('comunicados');
const modulo = ref('comunicados');
const title = ref('Comunicados Institucionales');

// Configuración de columnas para TableFilter
const columns = computed(() => {
    return [
        { key: 'actions', label: 'Acciones' },
        { key: 'title', label: 'Título' },
        { key: 'target_label', label: 'Destinatario' },
        { key: 'start_date', label: 'Fecha Inicio', filterType: 'date' },
        { key: 'end_date', label: 'Fecha Fin', filterType: 'date' },
        { key: 'requires_acceptance', label: 'Aceptación', filterType: 'select', isBoolean: true, booleanLabels: { true: 'Obligatoria', false: 'Informativo' }, align: 'center' },
        { key: 'file_type', label: 'Adjunto', align: 'center' },
        { key: 'status', label: 'Estado', filterType: 'select', isBoolean: true, booleanLabels: { true: 'Activo', false: 'Inactivo' }, align: 'center', filterDefault: '1' },
        { key: 'autor_name', label: 'Autor' },
    ];
});

// Modal de Previsualización rápida
const isPreviewModalOpen = ref(false);
const previewItem = ref(null);

const openPreview = (item) => {
    previewItem.value = item;
    isPreviewModalOpen.value = true;
};

// Alternar estado activo / inactivo
const toggleStatus = async (item) => {
    try {
        const response = await axios.put(route('comunicados-toggle-status', { id: item.id }));
        if (response.data.success) {
            item.status = response.data.status;
        }
    } catch (e) {
        console.error(e);
    }
};

// Eliminar comunicado
const deleteItem = (item) => {
    if (confirm(`¿Está seguro de eliminar el comunicado "${item.title}"?`)) {
        Inertia.delete(route('comunicados-delete', { id: item.id }), {
            preserveScroll: true,
        });
    }
};

const formatLocalDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
};
</script>

<template>
    <Head title="Comunicados" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                title="Comunicados Institucionales"
                subtitle="Gestión y emisión de comunicados para estudiantes, apoderados y personal escolar."
                icon="tabler:speakerphone"
                color="indigo"
                :actionsOnBottomOnMobile="false"
            >
                <template #actions>
                    <Buttons
                        v-if="canWrite"
                        label="Nuevo Comunicado"
                        icon="f7:plus-app-fill"
                        size="vuexy"
                        color="blue"
                        :href="route('comunicados-create')"
                        hideLabelOnMobile
                    />
                </template>
            </PremiumPageHeader>
        </template>

        <div class="space-y-6">
            <!-- Table Container Card -->
            <div class="bg-white dark:bg-slate-900 overflow-hidden border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 p-6">
                <TableFilter
                    :columns="columns"
                    :data="comunicados"
                    :title="title"
                    :ruta="rutaPrefijo"
                    top-scrollbar
                    manage-columns
                    exporteable
                    :useFilterAnio="useFilterAnio"
                    defaultSort="id"
                    defaultOrder="desc"
                >
                    <template #cell-actions="{ item }">
                        <div class="flex flex-row gap-1.5 w-full justify-center">
                            <!-- Previsualizar modal -->
                            <button
                                @click="openPreview(item)"
                                class="p-2 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 dark:hover:bg-indigo-600 dark:hover:text-white rounded-xl transition-all shadow-xs"
                                title="Previsualizar cómo lo verá el usuario"
                            >
                                <Icon icon="tabler:eye" class="w-4 h-4" />
                            </button>

                            <!-- Editar -->
                            <Link
                                v-if="canWrite"
                                :href="route('comunicados-show', { id: item.id })"
                                class="p-2 bg-amber-50 hover:bg-amber-600 hover:text-white text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-600 dark:hover:text-white rounded-xl transition-all shadow-xs"
                                title="Editar comunicado"
                            >
                                <Icon icon="tabler:pencil" class="w-4 h-4" />
                            </Link>

                            <!-- Alternar Estado -->
                            <button
                                v-if="canWrite"
                                @click="toggleStatus(item)"
                                class="p-2 rounded-xl transition-all shadow-xs"
                                :class="item.status ? 'bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400' : 'bg-slate-100 hover:bg-slate-600 hover:text-white text-slate-500 dark:bg-slate-800 dark:text-slate-400'"
                                :title="item.status ? 'Desactivar' : 'Activar'"
                            >
                                <Icon :icon="item.status ? 'tabler:check' : 'tabler:circle-off'" class="w-4 h-4" />
                            </button>

                            <!-- Eliminar -->
                            <button
                                v-if="canDelete"
                                @click="deleteItem(item)"
                                class="p-2 bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 dark:hover:bg-rose-600 dark:hover:text-white rounded-xl transition-all shadow-xs"
                                title="Eliminar comunicado"
                            >
                                <Icon icon="tabler:trash" class="w-4 h-4" />
                            </button>
                        </div>
                    </template>

                    <!-- Render de Destinatario -->
                    <template #cell-target_label="{ item }">
                        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                            :class="{
                                'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300': item.target_type === 'all',
                                'bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300': item.target_type === 'level',
                                'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300': item.target_type === 'classroom',
                                'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300': item.target_type === 'student',
                                'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300': item.target_type === 'role' || item.target_type === 'collaborator',
                            }">
                            <Icon v-if="item.target_type === 'all'" icon="tabler:world" class="w-3.5 h-3.5" />
                            <Icon v-else-if="item.target_type === 'level'" icon="tabler:school" class="w-3.5 h-3.5" />
                            <Icon v-else-if="item.target_type === 'classroom'" icon="tabler:chalkboard" class="w-3.5 h-3.5" />
                            <Icon v-else-if="item.target_type === 'student'" icon="tabler:user" class="w-3.5 h-3.5" />
                            <Icon v-else icon="tabler:users-group" class="w-3.5 h-3.5" />
                            <span>{{ item.target_label }}</span>
                        </span>
                    </template>

                    <!-- Render de Adjunto -->
                    <template #cell-file_type="{ item }">
                        <div v-if="item.file_path" class="flex items-center justify-center">
                            <a :href="'/documents/' + item.file_path" target="_blank" class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition" :title="item.file_name">
                                <Icon v-if="item.file_type === 'pdf'" icon="fa6-solid:file-pdf" class="text-rose-500 text-base" />
                                <Icon v-else-if="item.file_type === 'image'" icon="fa6-solid:file-image" class="text-blue-500 text-base" />
                                <Icon v-else icon="fa6-solid:file-lines" class="text-slate-500 text-base" />
                            </a>
                        </div>
                        <span v-else class="text-slate-300 dark:text-slate-600">-</span>
                    </template>
                </TableFilter>
            </div>
        </div>

        <!-- MODAL DE PREVISUALIZACIÓN -->
        <CardBoxModal v-model="isPreviewModalOpen" title="Vista Previa del Comunicado" sizeModal="medium" no-buttons hasCancel>
            <div v-if="previewItem" class="space-y-4">
                <div class="border-b pb-3">
                    <span class="text-[11px] font-bold uppercase text-indigo-600 tracking-wider">
                        Destinatario: {{ previewItem.target_label }}
                    </span>
                    <h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mt-1">
                        {{ previewItem.title }}
                    </h3>
                    <div class="flex items-center gap-3 text-xs text-slate-400 mt-1">
                        <span v-if="previewItem.start_date">Publicado: {{ formatLocalDate(previewItem.start_date) }}</span>
                        <span v-if="previewItem.end_date">Vence: {{ formatLocalDate(previewItem.end_date) }}</span>
                    </div>
                </div>

                <!-- Imagen en cuerpo si está habilitada -->
                <div v-if="previewItem.show_image_in_body && previewItem.file_path && previewItem.file_type === 'image'" class="rounded-2xl overflow-hidden border border-slate-200 shadow-sm max-h-[350px] flex items-center justify-center bg-slate-50">
                    <img :src="'/documents/' + previewItem.file_path" :alt="previewItem.title" class="object-contain max-h-[350px] w-full" />
                </div>

                <!-- Descripción / Texto -->
                <div class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    {{ previewItem.description || 'Sin contenido de texto adicional.' }}
                </div>

                <!-- Archivo adjunto descargable -->
                <div v-if="previewItem.file_path" class="p-3 bg-blue-50/60 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-900/40 flex items-center justify-between">
                    <div class="flex items-center gap-2.5 truncate pr-2">
                        <Icon v-if="previewItem.file_type === 'pdf'" icon="fa6-solid:file-pdf" class="text-rose-500 text-lg shrink-0" />
                        <Icon v-else-if="previewItem.file_type === 'image'" icon="fa6-solid:file-image" class="text-blue-500 text-lg shrink-0" />
                        <Icon v-else icon="fa6-solid:file" class="text-slate-500 text-lg shrink-0" />
                        <span class="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{{ previewItem.file_name }}</span>
                    </div>
                    <a :href="'/documents/' + previewItem.file_path" target="_blank" class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition shrink-0">
                        Ver archivo
                    </a>
                </div>

                <!-- Check de Aceptación -->
                <div v-if="previewItem.requires_acceptance" class="pt-3 border-t">
                    <label class="flex items-center gap-3 p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs text-amber-900 font-medium cursor-pointer">
                        <input type="checkbox" checked disabled class="w-4 h-4 rounded text-blue-600" />
                        <span>{{ previewItem.acceptance_text || 'He leído y acepto el comunicado' }}</span>
                    </label>
                </div>
            </div>
        </CardBoxModal>
    </BreezeAuthenticatedLayout>
</template>
